import React from 'react';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';

interface InputFieldProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  mask?: (value: string) => string;
  error?: string;
  help?: string;
  disabled?: boolean;
  maxLength?: number;
  success?: boolean;
  hideBottomMargin?: boolean;
  readOnly?: boolean;
}

// Estilos compartilhados pelos campos do formulário (foco azul; vermelho só para erro)
export const campoBase =
  'w-full rounded-xl border bg-white text-[15px] text-slate-900 placeholder-slate-400 transition-colors focus:outline-none focus:ring-2';
export const campoEstado = (error?: string, valido?: boolean) =>
  error
    ? 'border-red-500 bg-red-50/40 focus:border-red-600 focus:ring-red-500/20'
    : valido
      ? 'border-emerald-500 focus:border-[#0B57D0] focus:ring-[#0B57D0]/20'
      : 'border-slate-300 hover:border-slate-400 focus:border-[#0B57D0] focus:ring-[#0B57D0]/20';

export const RotuloCampo: React.FC<{ htmlFor: string; label: string; required?: boolean; valido?: boolean }> = ({ htmlFor, label, required, valido }) => (
  <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-1.5">
    {label}
    {required && (
      <>
        <span className="text-red-600" aria-hidden="true">*</span>
        <span className="sr-only">(obrigatório)</span>
      </>
    )}
    {valido && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" aria-label="preenchido" />}
  </label>
);

export const MensagemCampo: React.FC<{ name: string; error?: string; help?: string }> = ({ name, error, help }) => (
  <>
    {error && (
      <p id={`${name}-error`} className="mt-1.5 flex items-start gap-1.5 text-sm text-red-700 font-medium" role="alert">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>{error}</span>
      </p>
    )}
    {help && !error && (
      <p id={`${name}-help`} className="mt-1.5 flex items-start gap-1.5 text-sm text-slate-500">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>{help}</span>
      </p>
    )}
  </>
);

export const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  mask,
  error,
  help,
  disabled = false,
  maxLength,
  success = false,
  hideBottomMargin = false,
  readOnly = false,
}) => {
  const safeValue = value ?? '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mask) e.target.value = mask(e.target.value);
    onChange(e);
  };

  const isValid = success && safeValue.length > 0 && !error;

  return (
    <div className={hideBottomMargin ? '' : 'mb-6'}>
      <RotuloCampo htmlFor={name} label={label} required={required} valido={isValid} />
      <input
        id={name}
        name={name}
        type={type}
        value={safeValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={maxLength}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : help ? `${name}-help` : undefined}
        className={`${campoBase} h-12 px-4 ${campoEstado(error, isValid)}
          ${disabled ? '!bg-slate-100 text-slate-500 cursor-not-allowed !border-slate-200' : ''}
          ${readOnly ? '!bg-slate-50 text-slate-700 cursor-default' : ''}`}
      />
      <MensagemCampo name={name} error={error} help={help} />
      {maxLength && (
        <p className="mt-1 text-xs text-slate-500 text-right tabular-nums">
          {safeValue.length} / {maxLength}
        </p>
      )}
    </div>
  );
};

export default InputField;
